import {
  BRIDGE_DATE_OF_BIRTH_KEY,
  BRIDGE_INITIAL_BMI_EVIDENCE_KEY,
  BRIDGE_INITIAL_BMI_KEY,
} from './clinical-prefill';
import {
  MedicareBridgeChecklistItem,
  MedicareBridgeResponseValue,
} from './medicare-bridge.types';

const SCREENING_KEYS = [
  'treatment_purpose',
  'alternate_part_d_indication',
  'therapy_course',
  'therapy_start_date',
] as const;
const BMI_KEYS = [
  BRIDGE_INITIAL_BMI_KEY,
  'qualifying_conditions_bmi_30',
  'qualifying_conditions_bmi_27',
  'systolic_bp',
  'diastolic_bp',
  'antihypertensive_one',
  'antihypertensive_two',
] as const;
const PLAN_KEYS = ['lifestyle_plan', 'requested_product'] as const;

export type IntakeStopCode =
  | 'under_18'
  | 'cardiovascular_risk_reduction'
  | 'part_d_indication'
  | 'bmi_below_27';

export interface IntakeStop {
  code: IntakeStopCode;
  message: string;
}

export interface IntakeSections {
  screening: MedicareBridgeChecklistItem[];
  bmi: MedicareBridgeChecklistItem[];
  plan: MedicareBridgeChecklistItem[];
  other: MedicareBridgeChecklistItem[];
  preview: boolean;
}

/** Known provider-intake keys, in the preview's section order. Everything else stays in checklist order. */
export function groupIntakeItems(
  items: MedicareBridgeChecklistItem[],
): IntakeSections {
  const byKey = new Map(items.map((item) => [item.key, item]));
  const used = new Set<string>();
  const take = (keys: readonly string[]) =>
    keys.flatMap((key) => {
      const item = byKey.get(key);
      if (!item) return [];
      used.add(key);
      return [item];
    });
  const screening = take(SCREENING_KEYS);
  const bmi = take(BMI_KEYS);
  const plan = take(PLAN_KEYS);
  used.add(BRIDGE_DATE_OF_BIRTH_KEY);
  used.add(BRIDGE_INITIAL_BMI_EVIDENCE_KEY);
  return {
    screening,
    bmi,
    plan,
    other: items.filter((item) => !used.has(item.key)),
    preview: screening.length + bmi.length + plan.length > 0,
  };
}

const CARDIOVASCULAR_RISK_REDUCTION = 'CARDIOVASCULAR_RISK_REDUCTION';

/**
 * Age and cardiovascular-event risk reduction are not checklist `requiredWhen`
 * rules. Cardiovascular risk reduction drops the later BMI, qualifying-condition,
 * and treatment-plan questions via `itemsAfterIntakeStop`.
 */
export function bridgeIntakeStop(input: {
  hasDateOfBirth: boolean;
  age: number | null;
  treatmentPurpose: MedicareBridgeResponseValue | undefined;
  alternate: MedicareBridgeResponseValue | undefined;
  bmi: number | null;
}): IntakeStop | null {
  if (input.hasDateOfBirth && input.age != null && input.age < 18) {
    return { code: 'under_18', message: 'Age under 18 stops the Bridge intake.' };
  }
  if (selected(input.treatmentPurpose).includes(CARDIOVASCULAR_RISK_REDUCTION)) {
    return {
      code: 'cardiovascular_risk_reduction',
      message: 'Cardiovascular-event risk reduction stops the Bridge intake.',
    };
  }
  if (selected(input.alternate).some((value) => value !== 'NONE')) {
    return {
      code: 'part_d_indication',
      message: 'This indication routes to Part D and stops the Bridge intake.',
    };
  }
  if (input.bmi != null && input.bmi < 27) {
    return { code: 'bmi_below_27', message: 'BMI under 27 stops the Bridge intake.' };
  }
  return null;
}

const OMITTED_AFTER_CARDIOVASCULAR = new Set<string>([
  ...BMI_KEYS,
  BRIDGE_INITIAL_BMI_EVIDENCE_KEY,
  ...PLAN_KEYS,
]);

/** Questions removed from the form and from the submitted review. */
export function itemsAfterIntakeStop(
  items: MedicareBridgeChecklistItem[],
  stop: IntakeStop | null,
): MedicareBridgeChecklistItem[] {
  if (stop?.code !== 'cardiovascular_risk_reduction') return items;
  return items.filter((item) => !OMITTED_AFTER_CARDIOVASCULAR.has(item.key));
}

export function missingClinicalRequirements(
  items: MedicareBridgeChecklistItem[],
  responses: Record<string, MedicareBridgeResponseValue>,
): string[] {
  return items
    .filter(
      (item) =>
        (item.required || item.requiredWhen) &&
        item.key !== BRIDGE_INITIAL_BMI_EVIDENCE_KEY &&
        !hasAnswer(responses[item.key]),
    )
    .map((item) => item.label);
}

function selected(value: MedicareBridgeResponseValue | undefined): string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value;
  return [];
}

function hasAnswer(value: MedicareBridgeResponseValue | undefined): boolean {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
}
